import React, { useState, useRef, ChangeEvent, KeyboardEvent } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
} from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { API_BASE_URL } from "../../config/api";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import { addloginData } from "../../redux/action";
import { Lock, Loader2, CheckCircle, ArrowLeft } from "lucide-react";

const OTP_LENGTH = 4;

export default function VerifyOtpPage(): React.ReactElement {
  const navigate = useNavigate();
  const location = useLocation();
  const contact: string = (location.state as any)?.contact || ""; // Can be mobile or email
  const token: string = (location.state as any)?.token || "";

  const [otp, setOtp] = useState<string[]>(Array(OTP_LENGTH).fill(""));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const dispatch = useDispatch();
  const { otpToken = "", id = "" } = useSelector(
    (state: any) => state.loginData || {}
  );
  const inputsRef = useRef<Array<HTMLInputElement | null>>([]);

  const handleChange = (index: number, value: string) => {
    if (!/^[0-9]?$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    if (value && index < OTP_LENGTH - 1) {
      inputsRef.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputsRef.current[index - 1]?.focus();
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const otpValue = otp.join("");
    if (otpValue.length < OTP_LENGTH) {
      setError("Please enter the complete OTP");
      return;
    }

    setError("");
    setLoading(true);
    try {
      // Determine if contact is email or mobile
      const isEmail = contact.includes("@");
      const payload = isEmail 
        ? { email: contact, mobile: "", username: "", otp: otpValue }
        : { mobile: contact, email: "", username: "", otp: otpValue };
      
      const res = await axios.post(
        `${API_BASE_URL}verifyOtp`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${otpToken}`,
          },
        }
      );

      console.log("OTP verified:", res.data);
      setSuccess(true);
      dispatch(addloginData(res?.data));
      
      // Navigate after a brief delay
      setTimeout(() => {
        navigate("/resetpassword", { state: { contact } });
      }, 1500);
    } catch (err: any) {
      setError(err.response?.data?.message || "Invalid OTP");
    } finally {
      setLoading(false);
    }
  };

  const otpValue = otp.join("");

  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-gradient-to-br from-blue-50 via-slate-50 to-indigo-50 p-4">
      {/* Background decorative elements */}
      <div className="absolute top-0 left-0 w-96 h-96 bg-gradient-to-br from-blue-400/10 to-cyan-400/10 rounded-full blur-3xl -z-10 hidden md:block"></div>
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-gradient-to-tl from-indigo-400/10 to-purple-400/10 rounded-full blur-3xl -z-10 hidden md:block"></div>

      <Card className="w-full max-w-md shadow-2xl border border-slate-200/60 hover:shadow-3xl transition-all duration-300">
        {/* Header with branding */}
        <div className="relative bg-gradient-to-br from-blue-950 via-blue-900 to-blue-800 p-8 rounded-t-2xl overflow-hidden">
          <div className="absolute inset-0 opacity-20">
            <div className="absolute top-2 right-2 w-20 h-20 bg-cyan-400/20 rounded-full blur-xl"></div>
            <div className="absolute bottom-2 left-2 w-16 h-16 bg-blue-400/20 rounded-full blur-xl"></div>
          </div>
          
          <div className="relative z-10 text-center">
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-cyan-300 via-blue-400 to-blue-600 flex items-center justify-center shadow-lg shadow-blue-500/50 hover:shadow-blue-600/70 transition-all hover:scale-110">
                <Lock className="text-white w-8 h-8" strokeWidth={1.5} />
              </div>
            </div>
            <CardTitle className="text-3xl font-bold text-white mb-2">Verify OTP</CardTitle>
            <CardDescription className="text-blue-200 text-base">
              Enter the 4-digit code sent to your account
            </CardDescription>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <CardContent className="space-y-6 pt-8 pb-6">
            {/* Error Alert */}
            {error && (
              <div className="p-4 bg-red-50 border border-red-200/60 rounded-xl flex gap-3 animate-shake">
                <div className="w-5 h-5 rounded-full bg-red-400/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                </div>
                <p className="text-red-700 text-sm font-medium">{error}</p>
              </div>
            )}

            {/* Success Alert */}
            {success && (
              <div className="p-4 bg-green-50 border border-green-200/60 rounded-xl flex gap-3">
                <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <p className="text-green-700 text-sm font-medium">OTP verified! Redirecting...</p>
              </div>
            )}

            {/* OTP Input Fields */}
            <div className="space-y-3">
              <label className="text-sm font-semibold text-slate-700 block">
                Enter 4-digit code
              </label>
              <div className="flex justify-center gap-3 sm:gap-4">
                {otp.map((digit, index) => (
                  <input
                    key={index}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => handleChange(index, e.target.value)}
                    onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => handleKeyDown(index, e)}
                    ref={(el) => { inputsRef.current[index] = el; }}
                    disabled={loading || success}
                    className="w-12 h-12 sm:w-14 sm:h-14 text-center text-2xl font-bold border-2 border-slate-300 rounded-xl focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-400/50 transition-all bg-white/80 placeholder:text-slate-300 disabled:opacity-50 disabled:bg-slate-50 hover:border-blue-300"
                  />
                ))}
              </div>
            </div>

            {/* Progress indicator */}
            <div className="flex gap-1 justify-center">
              {Array(OTP_LENGTH).fill(0).map((_, i) => (
                <div
                  key={i}
                  className={`h-1 rounded-full transition-all duration-300 ${
                    otp[i] ? "bg-gradient-to-r from-blue-500 to-cyan-500 w-3" : "bg-slate-200 w-2"
                  }`}
                />
              ))}
            </div>

            {/* Info */}
            <p className="text-xs text-slate-500 text-center">
              Didn't receive the code? Check your email or try again.
            </p>
          </CardContent>

          <CardFooter className="flex flex-col gap-4 pb-8">
            <Button 
              type="submit" 
              className="w-full py-3 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2 group active:scale-95"
              disabled={loading || success || otpValue.length < OTP_LENGTH}
            >
              {loading ? (
                <>
                  <Loader2 size={18} className="animate-spin" />
                  Verifying...
                </>
              ) : success ? (
                <>
                  <CheckCircle size={18} />
                  Verified!
                </>
              ) : (
                <>
                  <Lock size={18} className="group-hover:translate-x-1 transition-transform" />
                  Verify OTP
                </>
              )}
            </Button>

            <button
              type="button"
              className="text-sm font-medium text-blue-600 hover:text-blue-700 hover:underline decoration-blue-200 transition-all p-2 hover:bg-blue-50 rounded-lg w-full flex items-center justify-center gap-2 disabled:opacity-50"
              onClick={() => navigate("/forgotpassword")}
              disabled={loading || success}
            >
              <ArrowLeft size={16} />
              Back
            </button>
          </CardFooter>
        </form>

        {/* Footer info */}
        <div className="px-8 py-4 border-t border-slate-100 bg-slate-50/50">
          <p className="text-xs text-slate-500 text-center">
            Your verification code is secure and will expire in 10 minutes
          </p>
        </div>
      </Card>
    </div>
  );
}
